import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionListEditor from "../../helpdesk/automation/ActionListEditor";
import { HelpdeskRulesAPI } from "../../../api/helpdeskRules";

const EMPTY_RULE = {
  name: "",
  description: "",
  scope_type: "global",
  scope_id: null,
  event_type: "ticket.created",
  conditions: {
    all: [],
    any: [] },
  actions: [
    {
      action_type: "notify",
      action_order: 0,
      params: {},
    },
  ],
};

export default function RuleEditor({ mode, ruleId }) {
  const [rule, setRule] = useState(EMPTY_RULE);
  const [loading, setLoading] = useState(mode === "edit");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);
  const [testTicketId, setTestTicketId] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testRunning, setTestRunning] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [typeOptionsError, setTypeOptionsError] = useState("");
  useEffect(() => {
    let active = true;
    HelpdeskRulesAPI.listLifecycleOptions().then(data => {
      if (active) setTypeOptions(data.types || []);
    }).catch(() => { if (active) setTypeOptionsError("Ticket types could not be loaded. Saved conditions have been retained."); });
    return () => { active = false; };
  }, []);
  const [users,setUsers]=useState([]);
  useEffect(()=>{HelpdeskRulesAPI.listUsers().then(data=>setUsers(data.users||data||[])).catch(()=>setUsers([]));},[]);

  useEffect(() => {
    if (mode === "edit") {
      loadRule();
    }
  }, [mode, ruleId]);

  useEffect(() => {
      loadTriggers();
    }, []);

  useEffect(() => {
      loadFields();
    }, []);

    useEffect(() => {
      loadOperators();
    }, []);

    useEffect(() => {
      loadCategoryOptions();
      loadDepartmentOptions();
      loadPriorityOptions();
      loadStatusOptions();
      loadSchoolOptions();
      loadSubcategoryOptions();
    }, []);

    async function loadCategoryOptions() {
      const data = await HelpdeskRulesAPI.listCategoryOptions();
      setCategoryOptions(data.options || []);
    }

    async function loadDepartmentOptions() {
      const data = await HelpdeskRulesAPI.listDepartmentOptions();
      setDepartmentOptions(data.options || []);
    }

    async function loadPriorityOptions() {
      const data = await HelpdeskRulesAPI.listPriorityOptions();
      setPriorityOptions(data.options || []);
    }

    async function loadStatusOptions() {
      const data = await HelpdeskRulesAPI.listStatusOptions();
      console.log("STATUS OPTIONS", data);
      setStatusOptions(data.options || []);
    }

    async function loadSchoolOptions() {
      const data = await HelpdeskRulesAPI.listSchoolOptions();
      setSchoolOptions(data.options || []);
    }

    async function loadSubcategoryOptions() {
      const data = await HelpdeskRulesAPI.listSubcategoryOptions();
      setSubcategoryOptions(data.options || []);
    }

    async function loadTriggers() {
      const res = await HelpdeskRulesAPI.listEvents();
      setTriggers(res.events || []);
    }

  async function loadRule() {
    try {
      const data = await HelpdeskRulesAPI.get(ruleId);

      // Ensure conditions is always an object
      let conditions = data.conditions;

      if (typeof conditions === "string") {
        try {
          conditions = JSON.parse(conditions || "{}");
        } catch {
          conditions = {};
        }
      }

      setRule({
        ...data,
        conditions,
        actions:
          data.actions && data.actions.length > 0
            ? data.actions
            : [
                {
                  action_type: "notify",
                  action_order: 0,
                  params: {},
                },
              ],
      });
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    let conditions = rule.conditions;

    // Ensure conditions is always an object before sending
    if (typeof conditions === "string") {
      try {
        conditions = JSON.parse(conditions || "{}");
      } catch {
        conditions = {};
      }
    }

    const payload = {
      ...rule,
      conditions,
    };

    if (mode === "create") {
      await HelpdeskRulesAPI.create(payload);
    } else {
      await HelpdeskRulesAPI.update(ruleId, payload);
    }

    navigate("/helpdesk/settings/rules");
  }

  useEffect(() => {
    loadNotifications();
  }, []);

  async function loadFields() {
    const data = await HelpdeskRulesAPI.listFields();
    setFields(data.fields || []);
  }

  async function loadNotifications() {
    const data = await HelpdeskRulesAPI.listNotifications();
    setNotifications(data.notifications || []);
  }

  async function loadOperators() {
    const data = await HelpdeskRulesAPI.listOperators();
    setOperators(data.operators || []);
  }

  function addCondition(group = "all") {

    const updated = {
      ...rule,
      conditions: {
        ...rule.conditions,
        [group]: [
          ...(rule.conditions?.[group] || []),
          { field: "", operator: "", value: "" }
        ]
      }
    };

    setRule(updated);
  }

    const runRuleTest = async () => {
    if (!testTicketId) return;

    try {
      setTestRunning(true);

    const data = await HelpdeskRulesAPI.testRule({
      rule,
      ticketId: Number(testTicketId),
    });

    setTestResult(data);

    } catch (err) {
      console.error("Rule test failed", err);
    } finally {
      setTestRunning(false);
    }
  };

  if (loading) return <div>Loading rule…</div>;

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <h1 className="text-xl font-semibold">
        {mode === "create" ? "New rule" : "Edit rule"}
      </h1>

      <div className="space-y-2">
        <label className="block text-sm">Name</label>
        <input
          className="w-full border p-2"
          value={rule.name}
          onChange={(e) => setRule({ ...rule, name: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm">Description</label>
        <textarea
          className="w-full border p-2"
          value={rule.description || ""}
          onChange={(e) =>
            setRule({ ...rule, description: e.target.value })
          }
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm">Trigger</label>
          <select
            className="border p-2"
            value={rule.event_type}
            onChange={(e) =>
              setRule({ ...rule, event_type: e.target.value })
            }
          >
            {Array.isArray(triggers) &&
              triggers.map((t) => (
                <option key={t.id} value={t.id}>
                  {String(t.label)}
                </option>
              ))}
          </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm">Scope</label>
        <select
          className="border p-2"
          value={rule.scope_type}
          onChange={(e) =>
            setRule({ ...rule, scope_type: e.target.value })
          }
        >
          <option value="global">Global (all schools)</option>
          <option value="school">Single school</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm">Conditions</label>
        {typeOptionsError && <p role="alert">{typeOptionsError}</p>}

        {(rule.conditions?.all || []).map((c, index) => {

          const selectedField = fields.find(f => f.key === c.field);
          const fieldType = selectedField?.type || "text";
          const isCategoryField = c.field === "ticket.category_id";
          const isDepartmentField = c.field === "ticket.department_id";
          const isTicketTypeField = c.field === "ticket.ticket_type_id";
          const isPriorityField = c.field === "ticket.priority_id";
          const isStatusField = c.field === "ticket.status_id";
          const isSchoolField = c.field === "ticket.school_id";
          const isSubcategoryField = c.field === "ticket.subcategory_id";

          return (
          <div key={index} className="flex gap-2">

            <select
              className="border p-2"
              value={c.field}
              onChange={(e) => {
                const updated = rule.conditions.all.map(condition => ({ ...condition }));
                updated[index].field = e.target.value;

                setRule({
                  ...rule,
                  conditions: { ...rule.conditions, all: updated }
                });
              }}>
              <option value="">Field</option>

              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}

            </select>

            <select
              className="border p-2"
              value={c.operator || ""}
              onChange={(e) => {
                const updated = rule.conditions.all.map(condition => ({ ...condition }));
                updated[index].operator = e.target.value;

                setRule({
                  ...rule,
                  conditions: { ...rule.conditions, all: updated }
                });
              }}
            >
              <option value="">Operator</option>

              {operators.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>

            {isTicketTypeField ? (
              <select aria-label="ALL ticket type" className="border p-2" value={c.value ?? ""}
                onChange={e => {
                  const updated = rule.conditions.all.map((condition, i) => i === index ? { ...condition, value: e.target.value } : condition);
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}>
                <option value="">Select ticket type</option>
                {c.value && !typeOptions.some(type => String(type.id) === String(c.value)) && <option value={c.value}>Saved ticket type #{c.value}</option>}
                {typeOptions.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
              </select>
            ) : isPriorityField ? (
              <select
                className="border p-2"
                value={c.value || ""}
                onChange={(e) => {
                  const updated = rule.conditions.all.map(condition => ({ ...condition }));
                  updated[index].value = e.target.value;
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}
              >
                <option value="">Select priority</option>
                {priorityOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

            ) : isStatusField ? (
              <select
                className="border p-2"
                value={c.value || ""}
                onChange={(e) => {
                  const updated = rule.conditions.all.map(condition => ({ ...condition }));
                  updated[index].value = e.target.value;
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}
              >
                <option value="">Select status</option>
                {statusOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>

            ) : isSchoolField ? (
              <select
                className="border p-2"
                value={c.value || ""}
                onChange={(e) => {
                  const updated = rule.conditions.all.map(condition => ({ ...condition }));
                  updated[index].value = e.target.value;
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}
              >
                <option value="">Select school</option>
                {schoolOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>

            ) : isSubcategoryField ? (
              <select
                className="border p-2"
                value={c.value || ""}
                onChange={(e) => {
                  const updated = rule.conditions.all.map(condition => ({ ...condition }));
                  updated[index].value = e.target.value;
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}
              >
                <option value="">Select subcategory</option>
                {subcategoryOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>

            ) : isCategoryField ? (
              <select
                className="border p-2"
                value={c.value || ""}
                onChange={(e) => {
                  const updated = rule.conditions.all.map(condition => ({ ...condition }));
                  updated[index].value = e.target.value;
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}
              >
                <option value="">Select category</option>
                {categoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>

            ) : isDepartmentField ? (
              <select
                className="border p-2"
                value={c.value || ""}
                onChange={(e) => {
                  const updated = rule.conditions.all.map(condition => ({ ...condition }));
                  updated[index].value = e.target.value;
                  setRule({ ...rule, conditions: { ...rule.conditions, all: updated } });
                }}
              >
                <option value="">Select department</option>
                {departmentOptions.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>

              ) : fieldType === "number" ? (
          <input
            type="number"
            className="border p-2"
            value={c.value || ""}
            onChange={(e) => {
              const updated = rule.conditions.all.map(condition => ({ ...condition }));
              updated[index].value = e.target.value;

              setRule({
                ...rule,
                conditions: { ...rule.conditions, all: updated }
              });
            }}
          />
        ) : fieldType === "boolean" ? (
          <select
            className="border p-2"
            value={c.value || ""}
            onChange={(e) => {
              const updated = rule.conditions.all.map(condition => ({ ...condition }));
              updated[index].value = e.target.value;

              setRule({
                ...rule,
                conditions: { ...rule.conditions, all: updated }
              });
            }}
          >
            <option value="">Select</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        ) : (
          <input
            type="text"
            className="border p-2"
            value={c.value || ""}
            onChange={(e) => {
              const updated = rule.conditions.all.map(condition => ({ ...condition }));
              updated[index].value = e.target.value;

              setRule({
                ...rule,
                conditions: { ...rule.conditions, all: updated }
              });
            }}
          />
        )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => addCondition("all")}
        className="px-3 py-1 border rounded"
      >
        + Add ALL condition
      </button>
      </div>

      <div className="pt-4">

      <label className="block text-sm">Any condition can match</label>

      {(rule.conditions?.any || []).map((c, index) => {

        return (
          <div key={index} className="flex gap-2">

            <select
              className="border p-2"
              value={c.field}
              onChange={(e) => {
                const updated = rule.conditions.any.map(condition => ({ ...condition }));
                updated[index].field = e.target.value;

                setRule({
                  ...rule,
                  conditions: { ...rule.conditions, any: updated }
                });
              }}>
              <option value="">Field</option>

              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}

            </select>

            <select
              className="border p-2"
              value={c.operator || ""}
              onChange={(e) => {
                const updated = rule.conditions.any.map(condition => ({ ...condition }));
                updated[index].operator = e.target.value;

                setRule({
                  ...rule,
                  conditions: { ...rule.conditions, any: updated }
                });
              }}
            >
              <option value="">Operator</option>

              {operators.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>

            {c.field === "ticket.ticket_type_id" ? (
              <select aria-label="ANY ticket type" className="border p-2" value={c.value ?? ""}
                onChange={e => {
                  const updated = rule.conditions.any.map((condition, i) => i === index ? { ...condition, value: e.target.value } : condition);
                  setRule({ ...rule, conditions: { ...rule.conditions, any: updated } });
                }}>
                <option value="">Select ticket type</option>
                {c.value && !typeOptions.some(type => String(type.id) === String(c.value)) && <option value={c.value}>Saved ticket type #{c.value}</option>}
                {typeOptions.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
              </select>
            ) : <input
              type="text"
              className="border p-2"
              value={c.value ?? ""}
              onChange={(e) => {
                const updated = rule.conditions.any.map(condition => ({ ...condition }));
                updated[index].value = e.target.value;

                setRule({
                  ...rule,
                  conditions: { ...rule.conditions, any: updated }
                });
              }}
            />}

          </div>
        );
      })}

      <button
        type="button"
        onClick={() => addCondition("any")}
        className="px-3 py-1 border rounded"
      >
        + Add ANY condition
      </button>

      </div>

      <ActionListEditor actions={rule.actions||[]} onChange={actions=>setRule({...rule,actions})} notifications={notifications} statusOptions={statusOptions} categoryOptions={categoryOptions} subcategoryOptions={subcategoryOptions} users={users}/>

     <div className="pt-4 flex gap-2">
      <button
        onClick={save}
        className="px-4 py-2 bg-blue-600 text-white rounded"
      >
        Save
      </button>

      <button
        onClick={() => navigate("/helpdesk/settings/rules")}
        className="px-4 py-2 border rounded"
      >
        Cancel
      </button>
        </div>
        <div className="pt-6 border-t mt-6">
          <label className="block text-sm font-semibold mb-2">
            Test Rule
          </label>

          <div className="flex gap-2 mb-3">
            <input
              type="number"
              placeholder="Ticket ID"
              value={testTicketId}
              onChange={(e) => setTestTicketId(e.target.value)}
              className="border rounded px-2 py-1 text-sm w-32"
            />

            <button
              onClick={runRuleTest}
              disabled={testRunning}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
            >
              {testRunning ? "Testing..." : "Run Test"}
            </button>
          </div>

          {testResult && (
            <div className="text-sm bg-gray-50 border p-3 rounded">

              <div className="mb-2 font-semibold">
                Result: {testResult.matched ? "Rule Matched ✔" : "Rule Did Not Match ✖"}
              </div>

              {testResult.conditions && (
                <div className="mb-2">
                  <div className="font-semibold">Conditions</div>
                    {testResult.conditions.map((c, i) => (
                      <div key={i} className="border-b py-1">

                        <div>
                          <strong>{c.field}</strong>
                        </div>

                        <div className="text-xs text-gray-600">
                          expected: {String(c.expected)}
                        </div>

                        <div className="text-xs text-gray-600">
                          actual: {String(c.actual)}
                        </div>

                        <div>
                          {c.result ? "✔ matched" : "✖ failed"}
                        </div>

                      </div>
                    ))}
                </div>
              )}

              {testResult.actions && testResult.actions.length > 0 && (
                <div>
                  <div className="font-semibold">Actions</div>
                  {testResult.actions.map((a, i) => (
                    <div key={i}>• {a}</div>
                  ))}
                </div>
              )}

            </div>
          )}
        </div>
        <div className="pt-6">
        <label className="block text-sm font-semibold mb-2">
          Rule Preview
        </label>

        <pre className="bg-gray-100 p-4 text-xs overflow-auto rounded border">
          {JSON.stringify(rule, null, 2)}
        </pre>
      </div>
    </div>
);
}